import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createTask, getTaskById, sendNotification, updateTask } from "./AssignedTaskService";
import "./TaskForm.css";

const PRIORITY_OPTIONS = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "SOMEDAY"];
const STATUS_OPTIONS = ["PENDING", "IN_PROGRESS", "DONE", "CANCELLED"];

export default function TaskForm() {
  const nav = useNavigate();
  const { id } = useParams();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [status, setStatus] = useState("PENDING");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [assigneeInput, setAssigneeInput] = useState("");
  const [followUps, setFollowUps] = useState([]);
  const [followUpInput, setFollowUpInput] = useState("");
  const [sendEmailOnAssign, setSendEmailOnAssign] = useState(true);
  const [sendEmailNow, setSendEmailNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (id) loadTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTask = async () => {
    const t = await getTaskById(id);
    if (!t) return;
    setTitle(t.title ?? "");
    setDescription(t.description ?? "");
    setPriority(t.priority ?? "MEDIUM");
    setStatus(t.status ?? "PENDING");
    setStartDate(t.startDate ?? "");
    setEndDate(t.endDate ?? "");
    setAssignees(t.assignees ?? []);
    setFollowUps(t.followUps ?? []);
    setSendEmailOnAssign(t.sendEmailOnAssign ?? true);
  };

  const addAssignee = () => {
    const v = assigneeInput.trim();
    if (!v || assignees.includes(v)) return;
    setAssignees([...assignees, v]);
    setAssigneeInput("");
  };

  const removeAssignee = (val) => setAssignees(assignees.filter((a) => a !== val));

  const addFollowUp = () => {
    const v = followUpInput.trim();
    if (!v) return;
    setFollowUps([...followUps, v]);
    setFollowUpInput("");
  };

  const removeFollowUp = (idx) => setFollowUps(followUps.filter((_, i) => i !== idx));

  const updateFollowUp = (idx, val) =>
    setFollowUps(followUps.map((f, i) => (i === idx ? val : f)));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError("Title is required."); return; }
    if (!assignees.length) { setError("Add at least one assignee."); return; }
    setError("");
    setSaving(true);
    const payload = {
      title: title.trim(),
      description,
      priority,
      status,
      startDate: startDate || null,
      endDate: endDate || null,
      assignees,
      followUps,
      sendEmailOnAssign,
      sendEmailNow,
    };
    if (id) await updateTask(id, payload);
    else await createTask(payload);
    setSaving(false);
    nav("/tasks");
  };

  const handleNotifyNow = async () => {
    if (!id) return;
    setNotifying(true);
    await sendNotification(id);
    setNotifying(false);
  };

  return (
    <div className="task-form-wrap">
      <h2>{id ? "Edit Task" : "Assign New Task"}</h2>

      {error && <div className="task-form-error">{error}</div>}

      <form onSubmit={submit}>
        <Field label="Title *">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Review Q3 report"
            required
          />
        </Field>

        <Field label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="Brief context, goals, or instructions..."
          />
        </Field>

        <div className="task-form-row">
          <Field label="Priority">
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>{p.charAt(0) + p.slice(1).toLowerCase()}</option>
              ))}
            </select>
          </Field>

          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s.replace("_", " ")}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="task-form-row">
          <Field label="Start Date">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>

          <Field label="End / Due Date">
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>

        {/* ASSIGNEES */}
        <Field label="Assignees * (username or email)">
          <div className="task-form-tag-input-row">
            <input
              value={assigneeInput}
              onChange={(e) => setAssigneeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAssignee())}
              placeholder="Enter username or email, press Enter or Add"
            />
            <button type="button" className="task-form-add-btn" onClick={addAssignee}>Add</button>
          </div>
          <div className="task-form-tag-list">
            {assignees.map((a) => (
              <span key={a} className="task-form-tag">
                {a}
                <button type="button" onClick={() => removeAssignee(a)} className="task-form-tag-remove">×</button>
              </span>
            ))}
          </div>
        </Field>

        {/* FOLLOW-UPS */}
        <Field label="Follow-ups">
          <div className="task-form-tag-input-row">
            <input
              value={followUpInput}
              onChange={(e) => setFollowUpInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFollowUp())}
              placeholder="Add a follow-up note, press Enter or Add"
            />
            <button type="button" className="task-form-add-btn" onClick={addFollowUp}>Add</button>
          </div>
          {followUps.length > 0 && (
            <ul className="task-form-followup-list">
              {followUps.map((f, i) => (
                <li key={i} className="task-form-followup-item">
                  <input
                    value={f}
                    onChange={(e) => updateFollowUp(i, e.target.value)}
                    className="task-form-followup-input"
                  />
                  <button type="button" onClick={() => removeFollowUp(i)} className="task-form-tag-remove">×</button>
                </li>
              ))}
            </ul>
          )}
        </Field>

        {/* EMAIL OPTIONS */}
        <div className="task-form-email-section">
          <label className="task-form-checkbox-label">
            <input
              type="checkbox"
              checked={sendEmailOnAssign}
              onChange={(e) => setSendEmailOnAssign(e.target.checked)}
            />
            Send email notification when task is assigned
          </label>

          {!id && (
            <label className="task-form-checkbox-label">
              <input
                type="checkbox"
                checked={sendEmailNow}
                onChange={(e) => setSendEmailNow(e.target.checked)}
              />
              Also send email immediately on save
            </label>
          )}
        </div>

        <div className="task-form-actions">
          <button type="submit" className="task-form-submit-btn" disabled={saving}>
            {saving ? "Saving..." : id ? "Save Changes" : "Create & Assign"}
          </button>
          <button type="button" className="task-form-cancel-btn" onClick={() => nav("/tasks")}>
            Cancel
          </button>
          {id && (
            <button
              type="button"
              className="task-form-notify-btn"
              onClick={handleNotifyNow}
              disabled={notifying}
            >
              {notifying ? "Sending..." : "Send Follow-up Email Now"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="task-form-field">
      <label className="task-form-label">{label}</label>
      {children}
    </div>
  );
}
